import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import { Tabs, TabPanel } from './components/tabs';

import TemplateEditor from './TemplateEditor';
import ValueEditor from './ValueEditor';
import Loading from './Loading';
import PolicyForm from './PolicyForm';
import PortalForm from './PortalForm';
import ExecutionTargetSelect, { TargetRef } from './components/ExecutionTargetSelect';

import { apiGet, apiPost } from './api';
import { FiledType, IErrors, IMeta, IPolicy, ITemplate, IUserTemplateCategory, IVarPrompt } from './models';
import { parseTemplate, validatePolicy } from './parser';

import './PolicyEditor.scss'
import { getTemplateWithNewCategory, normalizeCategory, replaceYamlName, splitYamlName } from "./templates/category";
import { TerminalWorkspace, TerminalWorkspaceHandle } from "./TerminalWorkspace";

export const targetRefsToParam = (refs: TargetRef[]): string => {
    if (refs.some(r => r.type === 'all')) return 'all';
    return refs.map(r => r.id).join(',');
};

interface IPolicyResponse {
    meta: IMeta,
    policy: IPolicy,
    template: ITemplate,
}

interface IPermissionResponse {
    permission: Array<string>
    uem: boolean
    mdm: boolean
}

interface InventoryHostsProps {
    terminalRef: React.RefObject<TerminalWorkspaceHandle | null>;
    testHosts: TargetRef[];
    setTestHosts: (value: TargetRef[]) => void;
    verbose: boolean;
    setVerbose: (value: boolean) => void;
    onTest: () => void;
    onAbort: () => void;
    hideVerboseSwitch?: boolean;
    hideSaveWarning?: boolean;
}

export const InventoryHostsWidget: React.FC<InventoryHostsProps> = (props) => {
    return (
        <div className="inventory-hosts-widget">
            <div className="inventory-hosts-widget__row">
                <div className="inventory-hosts-widget__dropdown">
                    <ExecutionTargetSelect
                        multi
                        required
                        value={props.testHosts}
                        onChange={props.setTestHosts}
                    />
                </div>

                <div className="inventory-hosts-widget__actions">
                    <button className="rd-btn rd-btn--outline rd-btn--secondary rd-btn--medium" onClick={props.onTest}>
                        <span className="rd-icon-onoff" />
                        Запустить
                    </button>
                    <button className="rd-btn rd-btn--outline rd-btn--secondary rd-btn--medium" onClick={props.onAbort}>
                        <span className="rd-icon-ban" />
                        Остановить
                    </button>
                </div>
            </div>

            <div className="inventory-hosts-widget__footer">
                <div className="inventory-hosts-widget__left">
                    {!props.hideVerboseSwitch && (
                        <label className="rd-switch rd-switch--label-after">
                            <input
                                type="checkbox"
                                className="rd-switch__input"
                                checked={props.verbose}
                                onChange={() => props.setVerbose(!props.verbose)}
                            />
                            <span className="rd-switch__track">
                                <span className="rd-switch__knob" />
                            </span>
                            <span className="rd-switch__label">Отладка</span>
                        </label>
                    )}
                </div>
                {!props.hideSaveWarning && (
                    <span className="inventory-hosts-widget__warning">
                        Внимание: перед запуском политика будет сохранена
                    </span>
                )}
            </div>
        </div>
    );
};

function PolicyEditor({ type_request }: { type_request: string }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const template_name = searchParams.get("template");
    const { id } = useParams();

    const [template, setTemplate] = useState<ITemplate>();
    const [policy, setPolicy] = useState<IPolicy>();
    const [meta, setMeta] = useState<IMeta>();
    const [errors, setErrors] = useState<IErrors>({ fields: {}, ok: true });
    const [tab, setTab] = useState<string>('policy');
    const [permissions, setPermissions] = useState<Array<string>>(['']);
    const [canChange, setCanChange] = useState<boolean>(false);
    const [hasUem, setHasUem] = useState<boolean>(false);
    const [hasMdm, setHasMdm] = useState<boolean>(false);

    const terminalWorkspaceRef = useRef<TerminalWorkspaceHandle>(null!);

    const [testHosts, setTestHosts] = useState<TargetRef[]>([]);
    const [userTemplateCategory, setUserTemplateCategory] = useState<IUserTemplateCategory>({
        selectedCategory: "",
        inputtedCategory: ""
    });
    const [verbose, setVerbose] = useState<boolean>(true);

    useEffect(() => {
        setErrors({ ...errors, categoryError: undefined });
        if (userTemplateCategory.selectedCategory && userTemplateCategory.inputtedCategory) {
            setErrors({
                ...errors,
                categoryError: Error("Вы можете либо выбрать категорию из существующих, либо создать новую!")
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userTemplateCategory]);

    useEffect(() => {
        apiGet<IPolicyResponse>('/automation/api/policy/', { template: template_name, id: id }).then((resp) => {
            setPolicy(resp.policy);
            setTemplate(resp.template);
        }).catch((error) => {
            setErrors({ ok: false, apiError: error, fields: {} });
        });
    }, [id, template_name]);

    useEffect(() => {
        setErrors({ ...errors, parseError: undefined } as IErrors)
        if (template) {
            try {
                setMeta(parseTemplate(template));
                onValueChange("template", template.id);
            } catch (error) {
                setErrors({ ...errors, parseError: error } as IErrors);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template]);

    useEffect(() => {
        // Check syntax playbook
        if (template && !Boolean(errors?.parseError)) {
            const handler = setTimeout(() => {
                apiPost("/automation/api/template/syntax_check/", { "data": template.yaml }).catch((e: any) => {
                    setErrors({ ...errors, parseError: Error(e.params.error) } as IErrors);
                })
            }, 500);
            return () => {
                clearTimeout(handler);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meta])

    useEffect(() => {
        apiGet<IPermissionResponse>('/api/permissions/', { type_request: type_request }).then((resp) => {
            if (permissions.length === 1 && permissions[0] === '') {
                setHasUem(resp.uem)
                setHasMdm(resp.mdm)
                if (type_request === 'change') {
                    setCanChange(resp.permission.includes('automation.change_policy'));
                } else if (type_request === 'create') {
                    setCanChange(resp.permission.includes('automation.add_policy'));
                }
                setPermissions(resp.permission);
            }
        }).catch((error) => {
            if (error.error_type === 4) {
                navigate('/forbidden/');
            }
            setErrors({ ok: false, apiError: error, fields: {} });
        });
    }, [permissions, navigate, type_request, canChange]);

    const setVar = (name: string, value: any) => {
        if (!policy) return;
        let vars = policy.vars;
        vars[name] = value;
        setPolicy({ ...policy, vars } as IPolicy)
    }

    const getPrompt = (field_name: string): IVarPrompt | null => {
        if (meta) for (let promt of meta.vars_prompt) {
            if (promt.name === field_name) {
                return promt
            }
        }
        return null;
    }

    const onValueChange = (name: string, value: any) => {
        if (!policy) return;
        const varPrompt = getPrompt(name);
        if (varPrompt && varPrompt.type === FiledType.File && value && value.package) {
            let vars = policy.vars;
            vars.package = value.package;
            setPolicy({ ...policy, file: value, vars } as IPolicy)
        } else if (["name", "template", "file", "show_on_portal", "auto_execute", "show_in_app_store", "app_icon", "app_name", "app_description", "os_name", "app_category"].includes(name)) {
            setPolicy({ ...policy, [name]: value } as IPolicy)
        } else if (["selectedCategory", "inputtedCategory"].includes(name)) {
            setUserTemplateCategory({ ...userTemplateCategory, [name]: value });
        } else {
            setVar(name, value);
        }
    }

    const handleTab = (errors: IErrors) => {
        if (errors.fields["app_name"]) {
            setTab("portal")
        } else {
            setTab("policy")
        }
    }

    const onSave = () => {
        if (!policy || !meta) return;
        const errors = validatePolicy(policy, meta);
        if (errors.ok) {
            const updatedTemplate = getTemplateWithNewCategory(template, userTemplateCategory);

            apiPost<IPolicy>("/automation/api/policy/", {
                "policy": policy,
                "template": updatedTemplate
            }).then(result => {
                setPolicy(result);
                setTemplate({ ...updatedTemplate, changed: false } as ITemplate);
                window.location.href = '/automation/policy/'
            })
        } else {
            setErrors(errors);
            handleTab(errors);
        }
    }

    const onSaveTemplate = () => {
        const [category, name] = template ? splitYamlName(template.yaml) : ["", ""];

        // @ts-ignore
        const updatedTemplate = {
            ...template,
            yaml: replaceYamlName(template?.yaml, `${category ? normalizeCategory(category) : "Пользовательские"} \\ ${name}`)
        } as ITemplate || undefined;

        apiPost<ITemplate>("/automation/api/template/", updatedTemplate).then(result => {
            setTemplate({ ...updatedTemplate, changed: false } as ITemplate);
        })
    }

    const startExecuteTerminal = (policy_id: any, host: string, verbose: boolean) => {
        terminalWorkspaceRef.current?.getExecuteTab()?.execute({
            instance: "policyExecutor",
            params: { kind: "execute_policy", "policy_id": policy_id, "host": host, "verbose": verbose },
        });
    }

    const onAbort = () => {
        terminalWorkspaceRef.current?.getExecuteTab()?.abort();
    }

    const onTest = () => {
        if (!policy || !meta || testHosts.length === 0) return;
        const host = targetRefsToParam(testHosts);
        const errors = validatePolicy(policy, meta);
        if (errors.ok) {
            const updatedTemplate = getTemplateWithNewCategory(template, userTemplateCategory);

            if (canChange) {
                apiPost<IPolicy>("/automation/api/policy/", { "policy": policy, "template": template, "is_test": true }).then(result => {
                    setPolicy(result);
                    setTemplate({ ...updatedTemplate, changed: false } as ITemplate);
                    startExecuteTerminal(result.id, host, verbose)
                })
            } else {
                startExecuteTerminal(policy.id, host, verbose)
            }
        } else {
            setErrors(errors);
            handleTab(errors)
        }
    }

    const renderError = (error: Error | undefined) => {
        return error && <Alert variant="danger">{error.message}</Alert>;
    }

    const hasBlockErrors = () => {
        return Boolean(errors?.categoryError || errors?.parseError);
    }

    return (
        <>
            {policy ?
                <div className="shadow-card">
                    {renderError(errors?.categoryError)}
                    {renderError(errors?.parseError)}
                    <Tabs
                        tabs={[
                            { key: 'policy', title: 'Политика' },
                            { key: 'portal', title: 'Портал самообслуживания' },
                            { key: 'data', title: 'Данные' },
                            { key: 'template', title: 'Шаблон' },
                            ...(permissions.includes('peers.can_execute_politics')
                                ? [{ key: 'test', title: 'Тестовый запуск' }]
                                : []),
                        ]}
                        activeKey={tab}
                        onSelect={(k) => {
                            setTab(k);
                            onAbort();
                        }}
                    />
                    <TabPanel active={tab === 'policy'} className="policy-form">
                        {meta && <PolicyForm
                            meta={meta}
                            policy={policy}
                            template={template}
                            userTemplateCategory={userTemplateCategory}
                            errors={errors}
                            canChange={canChange}
                            onChange={onValueChange}
                        />}
                    </TabPanel>
                    <TabPanel active={tab === 'portal'} className="policy-form">
                        {meta && <PortalForm
                            policy={policy}
                            canChange={canChange}
                            onChange={onValueChange}
                            errors={errors}
                            hasMdm={hasMdm}
                        />}
                    </TabPanel>
                    <TabPanel active={tab === 'data'} className="policy-form">
                        <ValueEditor value={policy} />
                    </TabPanel>
                    <TabPanel active={tab === 'template'}>
                        {template && <TemplateEditor template={template} canChange={canChange} hasUem={hasUem}
                            create_by={permissions.includes('automation.add_policy')}
                            onChange={setTemplate} onCreate={setTemplate} />}
                    </TabPanel>
                    {permissions.includes('peers.can_execute_politics') &&
                        <TabPanel active={tab === 'test'} className="policy-form">
                            <div style={{ padding: "10px" }}>
                                <TerminalWorkspace
                                    ref={terminalWorkspaceRef}
                                    id={"policyExecutor"}
                                    terminalHeight={"55vh"}
                                    activeJobData={{ "obj_id": (policy.id || "").toString() }}
                                    terminalInstance={"policyExecutor"}
                                />
                                <InventoryHostsWidget
                                    terminalRef={terminalWorkspaceRef}
                                    testHosts={testHosts}
                                    setTestHosts={setTestHosts}
                                    verbose={verbose}
                                    setVerbose={setVerbose}
                                    onTest={onTest}
                                    onAbort={onAbort}
                                />
                            </div>
                        </TabPanel>
                    }
                    <div style={{ marginTop: "10px", display: "flex", gap: "8px" }}>
                        <button className="rd-btn rd-btn--medium rd-btn--success" onClick={onSave}
                            disabled={hasBlockErrors() || !canChange}>Сохранить</button>
                        {template?.changed &&
                            <button className="rd-btn rd-btn--medium rd-btn--primary" onClick={onSaveTemplate}
                                disabled={hasBlockErrors()}>Сохранить шаблон</button>}
                        {policy.id &&
                            <a className={`rd-btn rd-btn--medium rd-btn--error${!permissions.includes('automation.delete_policy') ? ' disabled' : ''}`}
                                href={permissions.includes('automation.delete_policy') ? `/automation/policy/${policy.id}/delete/` : undefined}>Удалить</a>}
                        <a className="rd-btn rd-btn--medium rd-btn--secondary rd-btn--outline" href="/automation/policy/">Назад</a>
                    </div>
                </div> : errors && errors?.apiError ? <Alert variant='danger'>Ошибка загрузки</Alert> : <Loading />
            }
        </>
    );
}

export default PolicyEditor;
